import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ISLAND_BY_SLUG } from "../../lib/constants";

const STATUS_STYLES = {
  new:       { label: "New",       cls: "bg-[#E63946]/10 text-[#E63946]" },
  contacted: { label: "Contacted", cls: "bg-[#F5A524]/10 text-[#B45309]" },
  won:       { label: "Won",       cls: "bg-[#3BB273]/10 text-[#3BB273]" },
  lost:      { label: "Lost",      cls: "bg-[#6B7280]/10 text-[#6B7280]" },
};

export const SponsorsTab = () => {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/sponsor-inquiries");
      setInquiries(r.data);
    } catch { toast.error("Failed to load sponsor inquiries"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/sponsor-inquiries/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/sponsor-inquiries/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const visible = filter === "all"
    ? inquiries
    : inquiries.filter((i) => (i.status || "new") === filter);
  const counts = inquiries.reduce((acc, i) => {
    const s = i.status || "new"; acc[s] = (acc[s] || 0) + 1; return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="admin-sponsors-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "new", "contacted", "won", "lost"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            data-testid={`sponsor-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === s ? "bg-[#0AADA0] text-white" : "bg-[#FAFAFA] text-[#6B7280] hover:bg-[#F0F0F0]"
            }`}>
            {s === "all" ? `All (${inquiries.length})` : `${s} (${counts[s] || 0})`}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs text-[#0AADA0] hover:underline" data-testid="sponsor-refresh-btn">
          Refresh
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="card-soft p-8 text-center text-sm text-[#6B7280]">No inquiries yet.</div>
      ) : visible.map((i) => {
        const st = STATUS_STYLES[i.status || "new"] || STATUS_STYLES.new;
        return (
          <div key={i.id} className="card-soft p-5 space-y-3" data-testid={`sponsor-inquiry-${i.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium text-[#1A1A1A]">
                  {i.name}
                  {i.company && <span className="text-[#6B7280] font-normal"> · {i.company}</span>}
                </div>
                <div className="text-sm text-[#6B7280] mt-0.5">
                  <a href={`mailto:${i.email}`} className="text-[#0AADA0] hover:underline">{i.email}</a>
                  {i.phone && <span className="ml-2">· {i.phone}</span>}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${st.cls}`}>{st.label}</span>
              <span className="text-[11px] text-[#6B7280]">
                {i.created_at ? new Date(i.created_at).toLocaleString() : ""}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {i.slots?.length > 0 && (
                <div>
                  <div className="uppercase tracking-wider text-[#6B7280] mb-1">Interested in</div>
                  <div className="flex flex-wrap gap-1">
                    {i.slots.map((s) => (
                      <span key={s} className="px-2 py-0.5 rounded bg-[#FAFAFA] text-[#1A1A1A]">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {i.islands?.length > 0 && (
                <div>
                  <div className="uppercase tracking-wider text-[#6B7280] mb-1">Islands</div>
                  <div className="flex flex-wrap gap-1">
                    {i.islands.map((sl) => (
                      <span key={sl} className="px-2 py-0.5 rounded bg-[#FAFAFA] text-[#1A1A1A]">
                        {ISLAND_BY_SLUG[sl]?.flag} {ISLAND_BY_SLUG[sl]?.name || sl}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {i.budget && (
                <div>
                  <div className="uppercase tracking-wider text-[#6B7280] mb-1">Budget</div>
                  <div className="text-[#1A1A1A]">{i.budget}</div>
                </div>
              )}
            </div>

            {i.message && (
              <div className="text-sm text-[#1A1A1A] p-3 rounded-lg bg-[#FAFAFA] whitespace-pre-wrap">
                {i.message}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap pt-1">
              {["new", "contacted", "won", "lost"].map((s) => (
                <button key={s} onClick={() => setStatus(i.id, s)}
                  data-testid={`sponsor-set-${s}-${i.id}`}
                  disabled={(i.status || "new") === s}
                  className={`px-3 py-1 rounded text-xs transition-colors ${
                    (i.status || "new") === s
                      ? "bg-[#E5E5E5] text-[#9CA3AF] cursor-default"
                      : "bg-white border border-[#E5E5E5] text-[#1A1A1A] hover:bg-[#FAFAFA]"
                  }`}>
                  Mark {s}
                </button>
              ))}
              <button onClick={() => del(i.id)}
                data-testid={`sponsor-del-${i.id}`}
                className="ml-auto p-1.5 rounded text-[#6B7280] hover:bg-[#E63946]/10 hover:text-[#E63946]">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
